/**
 * @fileoverview Own draft creation/editing of matter-notification rules and explicit save/delete actions. Rule timing, required attributes, and Slack destinations come from separate backend catalogs. `NotificationRules.js` owns the refreshed catalog after changes; `kc-internal-backend/tasks/notification_rules.py` owns evaluation/delivery. Editing a rule and acknowledging a delivered notification are separate responsibilities.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import ToastContext from '../../ToastContext'
import api from '../../api'
import { AppBadge, AppButton, AppDropdown, AppModal, AppTextInput, AppToggle } from '../shared'
import { AdminNote } from './AdminChrome'

const stages = [
    'Pre-Demand',
    'Demand Sent',
    'Arbitration-AAA',
    'Arbitration-JAMS',
    'Arbitration-Other',
    'State Court',
    'Federal Court',
    'Appeal',
    'Post Judgment',
    'Settled/Resolved',
    'Withdrawn'
]

const substages = [
    'Complaint Filed',
    'Service Complete',
    'Motion to Dismiss Filed',
    'Discovery',
    'Stayed',
    'Dispositive Motion Pending',
    'Trial Upcoming',
    'Appeal',
    'Settled in Principal',
    'Agreement Executed & Payment(s) Pending',
    'Payments Complete/Fully Resolved'
]

const stateOptions = stages.concat(substages).map(stage => ({ label: stage, value: stage }))

const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
}

const toggleCardStyle = {
    border: '1px solid var(--surface-border)',
    borderRadius: 'var(--admin-radius-lg)',
    padding: '1rem',
    backgroundColor: 'var(--admin-muted-bg)',
}



function NotificationRuleForm({ ruleForm, setRuleForm, fetchRules }) {
    const showToast = useContext(ToastContext)
    const [slackChannels, setSlackChannels] = useState([])
    const [attributeTypes, setAttributeTypes] = useState([])
    const [ruleType, setRuleType] = useState('State')

    const resetForm = useCallback(

    () => {
        setRuleForm({
            visible: false,
            isEditing: false,
            caseAttributes: [],
            state: '',
            states: [],
            days: 0,
            slackChannel: '',
            id: null,
            oldValue: {},
        })
        setRuleType('State')
    }, [setRuleForm])

    const fetchSlackChannels = useCallback(

    async () => {
        try {
            const response = await api.get('/automations/slack-channels')
            const channels = (response.data?.allChannels || []).map(channel => ({
                label: channel.name,
                value: channel.id,
            }))
            setSlackChannels(channels)
        } catch (error) {
            console.error('Failed to fetch Slack channels:', error)
        }
    }, [])

    const fetchAttributeTypes = useCallback(

    async () => {
        try {
            const response = await api.get('/case_attribute_types')
            setAttributeTypes(Array.isArray(response.data) ? response.data : [])
        } catch (error) {
            console.error(error)
        }
    }, [])

    useEffect(() => {
        fetchSlackChannels()
        fetchAttributeTypes()
    }, [fetchAttributeTypes, fetchSlackChannels])

    useEffect(() => {
        if (!ruleForm.visible) return
        const nextType = Array.isArray(ruleForm.caseAttributes) && ruleForm.caseAttributes.length > 0 ? 'Attribute' : 'State'
        setRuleType(nextType)
    }, [ruleForm.visible, ruleForm.caseAttributes])

    const exampleText = useMemo(() => {
        const channelLabel = slackChannels.find(channel => channel.value === ruleForm.slackChannel)?.label || 'the selected Slack destination'
        if (ruleType === 'Attribute') {
            const states = (ruleForm.states || []).filter(Boolean).join(', ') || 'the selected states'
            const attributes = (ruleForm.caseAttributes || []).filter(Boolean).join(', ') || 'the selected attributes'
            return `Send an alert to ${channelLabel} when a case stays in ${states} for at least ${ruleForm.days || 0} days and is still missing ${attributes}.`
        }
        return `Send an alert to ${channelLabel} when a case stays in ${ruleForm.state || 'the selected state'} for at least ${ruleForm.days || 0} days.`
    }, [ruleForm.caseAttributes, ruleForm.days, ruleForm.slackChannel, ruleForm.state, ruleForm.states, ruleType, slackChannels])

    const canSubmit = useMemo(

    () => {
        if (!ruleForm.slackChannel) return false
        if (ruleForm.days === null || ruleForm.days === undefined || ruleForm.days === '') return false
        if (ruleType === 'State') {
            return Boolean(ruleForm.state)
        }
        return (ruleForm.states || []).length > 0 && (ruleForm.caseAttributes || []).length > 0
    }, [ruleForm.caseAttributes, ruleForm.days, ruleForm.slackChannel, ruleForm.state, ruleForm.states, ruleType])

    const submit =

    async () => {
        try {
            const selectedChannel = slackChannels.find(channel => channel.value === ruleForm.slackChannel)
            const payload = {
                SlackChannelName: selectedChannel?.label || '',
                SlackChannelId: ruleForm.slackChannel,
                Days: Number(ruleForm.days) || 0,
            }

            if (ruleType === 'State') {
                payload.State = ruleForm.state
            } else {
                payload.State = (ruleForm.states || []).join(',')
                payload.CaseAttributes = (ruleForm.caseAttributes || []).join(',')
            }

            if (ruleForm.isEditing && ruleForm.id) {
                payload.id = ruleForm.id
            }

            await api.post('/notification_rules', payload)
            await fetchRules()
            showToast({ severity: 'success', summary: 'Successful', detail: 'Notification Rule Saved' })
            resetForm()
        } catch (error) {
            console.error('Error:', error)
            showToast({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to save notification rule' })
        }
    }

    const deleteRule =

    async () => {
        if (!ruleForm.id) return
        try {
            await api.delete(`/notification_rules/${ruleForm.id}`)
            await fetchRules()
            showToast({ severity: 'success', summary: 'Successful', detail: 'Notification Rule Deleted' })
            resetForm()
        } catch (error) {
            console.error('Error:', error)
            showToast({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to delete notification rule' })
        }
    }

    return (
        <AppModal
            visible={ruleForm.visible}
            title={ruleForm.isEditing ? 'Edit Notification Rule' : 'Add Notification Rule'}
            onClose={resetForm}
            width="920px"
            footer={(
                <>
                    {ruleForm.isEditing && (
                        <AppButton variant='danger' label='Delete Rule' onClick={deleteRule} />
                    )}
                    <AppButton variant='secondary' label='Cancel' onClick={resetForm} />
                    <AppButton variant='success' label='Save Rule' onClick={submit} disabled={!canSubmit} />
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <AdminNote tone="neutral">
                    Configure either a state timer or a missing-attribute rule. The alert will be sent to the selected Slack destination once the rule conditions are met.
                </AdminNote>

                <div style={toggleCardStyle}>
                    <div className='flex align-items-center justify-content-between gap-3'>
                        <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>Rule Type</div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--text-color-secondary)', marginTop: '0.25rem' }}>
                                Toggle between a simple state duration rule and a missing-attribute reminder.
                            </div>
                        </div>
                        <div className='flex align-items-center gap-3'>
                            <AppBadge value={ruleType} severity={ruleType === 'Attribute' ? 'warning' : 'info'} />
                            <AppToggle
                                checked={ruleType === 'Attribute'}
                                onChange={(e) => {
                                    const nextType = e.value ? 'Attribute' : 'State'
                                    setRuleType(nextType)
                                    if (nextType === 'State') {
                                        setRuleForm(prev => ({ ...prev, states: [], caseAttributes: [] }))
                                    } else {
                                        setRuleForm(prev => ({ ...prev, state: '' }))
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div style={formGridStyle}>
                    <Field label="Slack Channel">
                        <AppDropdown
                            value={ruleForm.slackChannel}
                            options={slackChannels}
                            onChange={(e) => setRuleForm(prev => ({ ...prev, slackChannel: e.value }))}
                            placeholder="Select a Slack destination"
                        />
                    </Field>
                    <Field label="Days">
                        <AppTextInput
                            type="number"
                            value={ruleForm.days}
                            onChange={(e) => setRuleForm(prev => ({ ...prev, days: e.target?.value }))}
                        />
                    </Field>
                    {ruleType === 'State' && (
                        <Field label="State">
                            <AppDropdown
                                value={ruleForm.state}
                                options={stateOptions}
                                onChange={(e) => setRuleForm(prev => ({ ...prev, state: e.value }))}
                                placeholder="Select a state"
                            />
                        </Field>
                    )}
                </div>

                {ruleType === 'Attribute' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <Field label="States">
                            <SelectablePillGroup
                                options={stateOptions.map(option => option.value)}
                                selectedValues={ruleForm.states || []}
                                onToggle={(value) => {
                                    setRuleForm(prev => ({
                                        ...prev,
                                        states: toggleSelectedValue(prev.states || [], value),
                                    }))
                                }}
                            />
                        </Field>

                        <Field label="Case Attributes">
                            <SelectablePillGroup
                                options={attributeTypes.map(attribute => attribute.Attribute).filter(Boolean)}
                                selectedValues={ruleForm.caseAttributes || []}
                                onToggle={(value) => {
                                    setRuleForm(prev => ({
                                        ...prev,
                                        caseAttributes: toggleSelectedValue(prev.caseAttributes || [], value),
                                    }))
                                }}
                            />
                        </Field>
                    </div>
                )}

                <AdminNote tone="info" title="Example Alert">
                    {exampleText}
                </AdminNote>
            </div>
        </AppModal>
    )
}



function Field({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-color)' }}>{label}</span>
            {children}
        </label>
    )
}



function SelectablePillGroup({ options, selectedValues, onToggle }) {
    if (!options.length) {
        return (
            <div style={{ border: '1px dashed var(--surface-border)', borderRadius: 'var(--admin-radius-sm)', padding: '1rem', color: 'var(--text-color-secondary)' }}>
                No options available.
            </div>
        )
    }

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
            {options.map((option) => {
                const selected = selectedValues.includes(option)
                return (
                    <button
                        key={option}
                        type="button"
                        onClick={() => onToggle(option)}
                        style={{
                            border: selected ? '1px solid var(--admin-accent-soft-border)' : '1px solid var(--surface-border)',
                            backgroundColor: selected ? 'var(--admin-accent-soft-bg)' : 'var(--surface-card)',
                            color: selected ? 'var(--admin-accent-soft-text)' : 'var(--text-color)',
                            borderRadius: 'var(--admin-radius-sm)',
                            padding: '0.5rem 0.8rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        {option}
                    </button>
                )
            })}
        </div>
    )
}



function toggleSelectedValue(values, value) {
    return values.includes(value)
        ? values.filter(item => item !== value)
        : [...values, value]
}

export default NotificationRuleForm
